import api from './api';
// CommonState removed — using string literals 'active'/'inactive' directly

// Types
export interface SignatureConfig {
  role: string;
  title: string;
  signer_name: string;
}

export interface GeneralInfoData {
  company_name: string;
  company_name_en?: string;
  address: string;
  tax_code: string;
  phone?: string;
  email?: string;
  website?: string;
  fax?: string;
  legal_representative?: string;
  legal_representative_title?: string;
  legal_representative_id?: string;
  logo_url?: string;
  signature_url?: string;
  seal_url?: string;
  signatures?: SignatureConfig[];
  show_signature_names?: boolean;
}

export type DocType = 'PT' | 'PC' | 'NB' | 'JE' | 'BH' | 'HD' | 'INV' | 'PO' | 'SO' | 'PINV' | 'RET' | 'DCG' | 'DCT' | 'DCTT' | 'PR' | 'PD' | 'NKK' | 'XKK' | 'PNK' | 'PXK' | 'LR' | 'PCK' | 'KK' | 'WT' | 'COGS' | 'CCDC' | 'RCL' | 'LOT' | 'CPT' | 'TSCĐ' | 'PKT_BT' | 'PKT' | 'MDV' | 'POS_RETURN_REQUEST';
type ResetPeriod = 'day' | 'month' | 'year' | 'never';
const NUMBERING_RULES_PAGE_LIMIT = 100;

const DOC_TYPE_LABELS: Record<DocType, string> = {
  PT: 'Phiếu thu',
  PC: 'Phiếu chi',
  PKT: 'Nghiệp vụ khác',
  NB: 'Chuyển tiền nội bộ',
  JE: 'Phiếu kế toán',
  BH: 'Chứng từ bán hàng',
  HD: 'Hóa đơn điện tử (mã nội bộ)',
  INV: 'Quy tắc cũ INV',
  RET: 'Hóa đơn trả hàng',
  DCG: 'Điều chỉnh giảm',
  DCT: 'Điều chỉnh tăng',
  DCTT: 'Điều chỉnh thông tin',
  PO: 'Đơn đặt hàng',
  SO: 'Đơn bán hàng',
  PINV: 'Hoá đơn mua',
  MDV: 'Mua dịch vụ',
  PR: 'Trả hàng mua',
  PD: 'Giảm giá hàng mua',
  NKK: 'Nhập kho khác',
  XKK: 'Xuất kho khác',
  PNK: 'Phiếu nhập kho',
  PXK: 'Phiếu xuất kho',
  LR: 'Lệnh lắp ráp',
  PCK: 'Phiếu chuyển kho',
  KK: 'Kiểm kê kho',
  WT: 'Chuyển kho nội bộ',
  COGS: 'Tính giá vốn',
  CCDC: 'Công cụ dụng cụ',
  RCL: 'Thu hồi kho',
  LOT: 'Lô hàng',
  CPT: 'Chi phí trả trước',
  TSCĐ: 'Tài sản cố định',
  PKT_BT: 'Bù trừ công nợ',
  POS_RETURN_REQUEST: 'Yêu cầu hoàn POS',
};

export interface NumberingRule {
  doc_type: DocType;
  doc_type_label: string;
  prefix: string;
  number_length: number;
  reset_period: ResetPeriod;
  start_number: number;
  current_number: number;
  is_configured: boolean;
  preview?: string;
  scope?: string | null;
  scope_type?: string | null;
  scope_key?: string | null;
}

export type NumberingIntegrityCode = 'DUPLICATE_ACTIVE_PREFIX' | 'LEGACY_INVOICE_RULE_ACTIVE';

export interface NumberingIntegrityFinding {
  code: NumberingIntegrityCode;
  severity: 'blocking' | 'advisory';
  doc_types: string[];
  config_ids: string[];
  scope_key?: string;
  scope_keys?: string[];
  prefix?: string;
  requires_operator_decision: true;
}

interface NumberingRuleApiResponse {
  _id: string;
  tenant_id: string;
  doc_type: string;
  prefix: string;
  sequence_width: number;
  reset_every: 'day' | 'month' | 'year' | 'never' | 'quarter' | 'none';
  initial_sequence: number;
  current_sequence?: number;
  is_active: boolean;
  updated_at?: string;
  scope?: string | null;
  scope_type?: string | null;
  scope_key?: string | null;
}

// API Response wrapper
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  pagination?: { page: number; limit: number; total: number; total_pages: number };
  integrity?: NumberingIntegrityFinding[];
}

export interface InventoryOpeningImportJobStatus {
  job_id: string;
  status: 'uploading' | 'queued' | 'processing' | 'awaiting_confirmation' | 'completed' | 'failed';
  expected_count: number;
  received_count: number;
  progress_percent: number;
  progress_message: string | null;
  error: string | null;
  confirmation_products: Array<{ product_id: string; product_name: string; warehouse_id: string }>;
  journal_entry_id: string | null;
  total_value: number | null;
}

export type AccountingReadinessSeverity = 'critical' | 'high' | 'medium' | 'low';

export interface AccountingSettingsReadinessFinding {
  code: string;
  severity: AccountingReadinessSeverity;
  entity: string;
  record_id: string;
  store_id: string | null;
  evidence: Record<string, unknown>;
  recommended_action: string;
  auto_fix: false;
}

export interface AccountingSettingsReadinessSummary {
  critical: number;
  high: number;
  medium: number;
  low: number;
}

export interface AccountingSettingsReadinessData {
  findings: AccountingSettingsReadinessFinding[];
  summary: AccountingSettingsReadinessSummary;
}

export interface OpeningBalanceDetailsParams {
  store_id: string;
  page?: number;
  limit?: number;
  search?: string;
  warehouse_id?: string;
}

/**
 * Service for Accounting Settings
 * Handles: General Info, Numbering Rules, Fund Accounts, Currencies, etc.
 */
export const accountingSettingsService = {
  // ==================== Readiness Diagnostics ====================

  /**
   * Get read-only accounting settings diagnostics
   * GET /accounting/settings-readiness
   */
  getReadiness(): Promise<ApiResponse<AccountingSettingsReadinessData>> {
    return api.get('/accounting/settings-readiness').then(r => r.data);
  },

  // ==================== General Info ====================

  /**
   * Get general company information for accounting reports
   * GET /settings/business — TCR-1754: trước đây gọi `GET /settings` (composite
   * business+payment+einvoice+notifications), route đó bị gate
   * `settings.core.manage` (owner-only) trong 1 security fix cùng ngày
   * 2026-09-10 (cùng lớp bug TCR-1483). `getGeneralInfo()` chỉ thực sự cần
   * phần `business` — dùng qua `useSignatureConfig` ở 18+ trang báo cáo mà
   * MỌI vai trò có quyền xem báo cáo đều cần load được, không riêng owner.
   * `GET /settings/business` đã CỐ Ý không gác quyền từ TCR-1671 (Sidebar +
   * FaviconSync gọi ở mọi trang cho mọi role) và trả thẳng đúng object
   * business — không cần bóc `.business` như route composite cũ.
   */
   getGeneralInfo(): Promise<ApiResponse<GeneralInfoData>> {
    return api.get('/settings/business').then(r => r.data);
  },

  /**
   * Update general company information
   * PATCH /settings/business
   */
  updateGeneralInfo(data: GeneralInfoData): Promise<ApiResponse<GeneralInfoData>> {
    return api.patch('/settings/business', data).then(r => r.data);
  },

  // ==================== Numbering Rules ====================

  /**
   * Get all numbering rules for document types
   * GET /accounting/document-numbering/configs
   * API returns: { items: NumberingRule[], meta: {...} } — no `success` wrapper
   */
  getNumberingRules(): Promise<ApiResponse<NumberingRule[]>> {
    return api.get('/accounting/document-numbering/configs', {
      params: { page: 1, limit: NUMBERING_RULES_PAGE_LIMIT },
    }).then(r => {
      const res = r.data as unknown as {
        items: NumberingRuleApiResponse[];
        meta: Record<string, unknown>;
        integrity?: NumberingIntegrityFinding[];
      };
      const items = Array.isArray(res.items) ? res.items : [];
      const rules: NumberingRule[] = items.map((item): NumberingRule => ({
        doc_type: item.doc_type as DocType,
        doc_type_label: DOC_TYPE_LABELS[item.doc_type as DocType] || item.doc_type,
        prefix: item.prefix,
        number_length: item.sequence_width,
        reset_period: item.reset_every as ResetPeriod,
        start_number: item.initial_sequence,
        current_number: Math.max(
          0,
          (item.current_sequence ?? item.initial_sequence) - 1,
        ),
        is_configured: item.is_active,
        preview: undefined,
        ...(item.scope !== undefined ? { scope: item.scope } : {}),
        ...(item.scope_type !== undefined ? { scope_type: item.scope_type } : {}),
        ...(item.scope_key !== undefined ? { scope_key: item.scope_key } : {}),
      }));
      return {
        success: true,
        data: rules,
        ...(Array.isArray(res.integrity) ? { integrity: res.integrity } : {}),
      };
    });
  },

  /**
   * Get single numbering rule detail
   * GET /accounting/document-numbering/rules/:docType
   */
  getNumberingRule(docType: DocType): Promise<ApiResponse<NumberingRule>> {
    return api.get(`/accounting/document-numbering/configs/${docType}`).then(r => r.data);
  },

  /**
   * Update numbering rule for a document type
   * PUT /accounting/document-numbering/rules/:docType
   */
  updateNumberingRule(
    docType: DocType,
    data: Partial<NumberingRule>
  ): Promise<ApiResponse<NumberingRule>> {
    const payload: Record<string, unknown> = {};
    if (data.prefix !== undefined) payload.prefix = data.prefix;
    if (data.number_length !== undefined) payload.sequence_width = data.number_length;
    if (data.reset_period !== undefined) payload.reset_every = data.reset_period;
    if (data.start_number !== undefined) payload.initial_sequence = data.start_number;
    return api.put(`/accounting/document-numbering/configs/${docType}`, payload).then(r => r.data);
  },

  /**
   * Preview next document number
   * GET /accounting/document-numbering/rules/:docType/preview
   */
  peekNextNumber(docType: DocType): Promise<ApiResponse<{ number: string; docType: string; docDate: string }>> {
    return api.get(`/accounting/document-numbering/peek/${docType}`).then(r => r.data);
  },

  /**
   * Reset numbering counter to start_number
   * POST /accounting/document-numbering/rules/:docType/reset
   */
  resetNumberingRule(docType: DocType): Promise<ApiResponse<NumberingRule>> {
    return api.post(`/accounting/document-numbering/configs/${docType}/reset`).then(r => r.data);
  },

  // ==================== File Upload ====================

  /**
   * Upload logo, signature, or seal image
   * POST /upload
   */
  uploadFile(
    file: File,
    type: 'logo' | 'signature' | 'seal'
  ): Promise<ApiResponse<{ url: string }>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    formData.append('module', 'accounting');

    return api
      .post('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(r => r.data);
  },

  // ==================== Fund Accounts (Sổ quỹ) ====================

  /**
   * Get all fund accounts (cash funds)
   * GET /accounting/fund-accounts?type=cash
   */
  getFundAccounts(params?: { store_id?: string }): Promise<ApiResponse<any[]>> {
    return api.get('/accounting/fund-accounts', { params: { type: 'cash', ...params } }).then(r => r.data);
  },

  /**
   * Create new fund account
   * POST /accounting/fund-accounts
   */
  createFundAccount(data: Partial<any>): Promise<ApiResponse<any>> {
    return api.post('/accounting/fund-accounts', data).then(r => r.data);
  },

  /**
   * Update fund account
   * PUT /accounting/fund-accounts/:id
   */
  updateFundAccount(id: string, data: Partial<any>): Promise<ApiResponse<any>> {
    return api.put(`/accounting/fund-accounts/${id}`, data).then(r => r.data);
  },

  /**
   * Delete fund account
   * DELETE /accounting/fund-accounts/:id
   */
  deleteFundAccount(id: string): Promise<ApiResponse<void>> {
    return api.delete(`/accounting/fund-accounts/${id}`).then(r => r.data);
  },

  // ==================== Bank Accounts (TK Ngân hàng) ====================

  /**
   * Get all bank accounts
   * GET /accounting/fund-accounts?type=bank
   */
  getBankAccounts(params?: { store_id?: string }): Promise<ApiResponse<any[]>> {
    return api.get('/accounting/fund-accounts', { params: { type: 'bank', ...params } }).then(r => r.data);
  },

  /**
   * Create new bank account
   * POST /accounting/fund-accounts (type=bank)
   */
  createBankAccount(data: Partial<any>): Promise<ApiResponse<any>> {
    return api.post('/accounting/fund-accounts', { ...data, type: 'bank' }).then(r => r.data);
  },

  /**
   * Update bank account
   * PUT /accounting/fund-accounts/:id
   */
  updateBankAccount(id: string, data: Partial<any>): Promise<ApiResponse<any>> {
    return api.put(`/accounting/fund-accounts/${id}`, data).then(r => r.data);
  },

  /**
   * Delete bank account
   * DELETE /accounting/fund-accounts/:id
   */
  deleteBankAccount(id: string): Promise<ApiResponse<void>> {
    return api.delete(`/accounting/fund-accounts/${id}`).then(r => r.data);
  },

  // ==================== Inventory Item Groups ====================

  getInventoryItemGroups(): Promise<ApiResponse<any[]>> {
    return api.get('/accounting/inventory-item-groups').then(r => r.data);
  },

  createInventoryItemGroup(data: {
    code: string; name: string; nature: string;
    default_revenue_account_code?: string; default_cogs_account_code?: string;
    default_inventory_account_code?: string;
    default_warehouse_id?: string | null;
    default_purchase_expense_account_code?: string;
    default_line_discount_account_code?: string;
    default_sales_discount_account_code?: string;
    default_sales_return_account_code?: string;
    default_vat_rate_id?: string | null;
    hkd_industry_tax_rate_id?: string | null;
    import_tax_rate?: number | null;
    export_tax_rate?: number | null;
    excise_tax_group_id?: string | null;
  }): Promise<ApiResponse<any>> {
    return api.post('/accounting/inventory-item-groups', data).then(r => r.data);
  },

  updateInventoryItemGroup(id: string, data: Partial<any>): Promise<ApiResponse<any>> {
    return api.put(`/accounting/inventory-item-groups/${id}`, data).then(r => r.data);
  },

  deleteInventoryItemGroup(id: string): Promise<ApiResponse<void>> {
    return api.delete(`/accounting/inventory-item-groups/${id}`).then(r => r.data);
  },

  // ==================== Deferred Revenue ====================

  getDeferredRevenue(): Promise<ApiResponse<any[]>> {
    return api.get('/accounting/deferred-revenue').then(r => r.data);
  },

  allocateDeferredRevenue(id: string, data: { amount: number; allocation_date: string }): Promise<ApiResponse<any>> {
    return api.post(`/accounting/deferred-revenue/${id}/allocate`, data).then(r => r.data);
  },

  // ==================== Currencies ====================

  /**
   * Get all currencies
   * GET /accounting/currencies
   */
  getCurrencies(): Promise<ApiResponse<any[]>> {
    return api.get('/accounting/currencies').then(r => r.data);
  },

  /**
   * Create new currency
   * POST /accounting/currencies
   */
  createCurrency(data: Partial<any>): Promise<ApiResponse<any>> {
    const payload = {
      currency_code: data.code,
      currency_name: data.name,
      symbol: data.symbol,
      exchange_rate: String(data.current_rate ?? 0),
      effective_date: data.rate_date ? new Date(data.rate_date).toISOString() : new Date().toISOString(),
      is_base_currency: data.is_default ?? false,
    };
    return api.post('/accounting/currencies', payload).then(r => r.data);
  },

  /**
   * Update currency
   * PUT /accounting/currencies/:code
   */
  updateCurrency(code: string, data: Partial<any>): Promise<ApiResponse<any>> {
    return api.put(`/accounting/currencies/${code}`, data).then(r => r.data);
  },

  /**
   * Delete currency
   * DELETE /accounting/currencies/:code
   */
  deleteCurrency(code: string): Promise<ApiResponse<void>> {
    return api.delete(`/accounting/currencies/${code}`).then(r => r.data);
  },

  /**
   * Get exchange rate history for a currency
   * GET /accounting/currencies/:code/rates
   */
  getCurrencyRates(code: string): Promise<ApiResponse<any[]>> {
    return api.get(`/accounting/currencies/${code}/rates`).then(r => r.data);
  },

  /**
   * Add new exchange rate for a currency
   * POST /accounting/currencies/:code/rates
   */
  addCurrencyRate(code: string, data: { rate: number; date: string; note?: string }): Promise<ApiResponse<any>> {
    return api.post(`/accounting/currencies/${code}/rates`, data).then(r => r.data);
  },

  // ==================== Categories (Danh mục thu chi) ====================

  /**
   * Get categories by type (income/expense)
   * GET /accounting/categories?type=:type
   */
  getCategories(type: 'income' | 'expense'): Promise<ApiResponse<any[]>> {
    return api.get(`/accounting/categories?type=${type}`).then(r => r.data);
  },

  /**
   * Create new category
   * POST /accounting/categories
   */
  createCategory(data: Partial<any>): Promise<ApiResponse<any>> {
    return api.post('/accounting/categories', data).then(r => r.data);
  },

  /**
   * Update category
   * PUT /accounting/categories/:id
   */
  updateCategory(id: string, data: Partial<any>): Promise<ApiResponse<any>> {
    return api.put(`/accounting/categories/${id}`, data).then(r => r.data);
  },

  /**
   * Delete category
   * DELETE /accounting/categories/:id
   */
  deleteCategory(id: string): Promise<ApiResponse<void>> {
    return api.delete(`/accounting/categories/${id}`).then(r => r.data);
  },

  // ─── Cash Flow Items ──────────────────────────────────────

  getCashFlowItems(params?: { type?: string; activity?: string; is_active?: boolean; search?: string; page?: number; limit?: number }): Promise<ApiResponse<any>> {
    return api.get('/accounting/cash-flow-items', { params }).then(r => r.data);
  },

  getCashFlowItemOptions(type?: string): Promise<ApiResponse<any[]>> {
    return api.get('/accounting/cash-flow-items/options', { params: { type } }).then(r => {
      const data = r.data;
      if (Array.isArray(data)) {
        return { success: true, data };
      }
      return data;
    });
  },

  createCashFlowItem(data: Partial<any>): Promise<ApiResponse<any>> {
    return api.post('/accounting/cash-flow-items', data).then(r => r.data);
  },

  updateCashFlowItem(id: string, data: Partial<any>): Promise<ApiResponse<any>> {
    return api.put(`/accounting/cash-flow-items/${id}`, data).then(r => r.data);
  },

  updateCashFlowItemStatus(id: string, isActive: boolean): Promise<ApiResponse<any>> {
    return api.patch(`/accounting/cash-flow-items/${id}/status`, { is_active: isActive }).then(r => r.data);
  },

  deleteCashFlowItem(id: string): Promise<ApiResponse<void>> {
    return api.delete(`/accounting/cash-flow-items/${id}`).then(r => r.data);
  },

  seedCashFlowItemDefaults(): Promise<ApiResponse<any>> {
    return api.post('/accounting/cash-flow-items/seed-defaults').then(r => r.data);
  },

  // ─── VAT Rates ──────────────────────────────────────────────

  getVatRates(params?: { is_active?: boolean; search?: string; page?: number; limit?: number }): Promise<ApiResponse<any>> {
    return api.get('/accounting/vat-rates', { params }).then(r => r.data);
  },

  createVatRate(data: Partial<any>): Promise<ApiResponse<any>> {
    return api.post('/accounting/vat-rates', data).then(r => r.data);
  },

  updateVatRate(id: string, data: Partial<any>): Promise<ApiResponse<any>> {
    return api.put(`/accounting/vat-rates/${id}`, data).then(r => r.data);
  },

  updateVatRateStatus(id: string, isActive: boolean): Promise<ApiResponse<any>> {
    return api.patch(`/accounting/vat-rates/${id}/status`, { is_active: isActive }).then(r => r.data);
  },

  deleteVatRate(id: string): Promise<ApiResponse<void>> {
    return api.delete(`/accounting/vat-rates/${id}`).then(r => r.data);
  },

  // ─── VAT Reduction Period ──────────────────────────────────────

  getVatReductionPeriod(): Promise<ApiResponse<any>> {
    return api.get('/accounting/vat-reduction-period').then(r => r.data);
  },

  updateVatReductionPeriod(data: {
    effective_from: string;
    effective_to: string;
    reduction_percent?: number;
    note?: string;
    is_active?: boolean;
  }): Promise<ApiResponse<any>> {
    return api.put('/accounting/vat-reduction-period', data).then(r => r.data);
  },

  // ─── VAT Conversion Rules ──────────────────────────────────────

  getVatConversionRules(params?: { is_active?: boolean; search?: string; page?: number; limit?: number }): Promise<ApiResponse<any>> {
    return api.get('/accounting/vat-conversion-rules', { params }).then(r => r.data);
  },

  createVatConversionRule(data: Partial<any>): Promise<ApiResponse<any>> {
    return api.post('/accounting/vat-conversion-rules', data).then(r => r.data);
  },

  updateVatConversionRule(id: string, data: Partial<any>): Promise<ApiResponse<any>> {
    return api.put(`/accounting/vat-conversion-rules/${id}`, data).then(r => r.data);
  },

  deleteVatConversionRule(id: string): Promise<ApiResponse<void>> {
    return api.delete(`/accounting/vat-conversion-rules/${id}`).then(r => r.data);
  },

  // ─── HKD Industry Tax Rates ──────────────────────────────────────

  getHkdIndustryTaxRates(params?: { search?: string; page?: number; limit?: number }): Promise<ApiResponse<any>> {
    return api.get('/accounting/hkd-industry-tax-rates', { params }).then(r => r.data);
  },

  createHkdIndustryTaxRate(data: Partial<any>): Promise<ApiResponse<any>> {
    return api.post('/accounting/hkd-industry-tax-rates', data).then(r => r.data);
  },

  updateHkdIndustryTaxRate(id: string, data: Partial<any>): Promise<ApiResponse<any>> {
    return api.put(`/accounting/hkd-industry-tax-rates/${id}`, data).then(r => r.data);
  },

  deleteHkdIndustryTaxRate(id: string): Promise<ApiResponse<void>> {
    return api.delete(`/accounting/hkd-industry-tax-rates/${id}`).then(r => r.data);
  },

  // ─── HKD Tax Regime Rules ──────────────────────────────────────

  getHkdTaxRegimeRules(params?: { year?: number; include_inactive?: boolean }): Promise<ApiResponse<any>> {
    return api.get('/accounting/hkd-tax-regime/rules', { params }).then(r => r.data);
  },

  resolveHkdTaxRegime(params: { revenue: number; year?: number; hkd_industry_tax_rate_id?: string }): Promise<ApiResponse<any>> {
    return api.get('/accounting/hkd-tax-regime/rules/resolve', { params }).then(r => r.data);
  },

  // ─── HKD Tax Profile ──────────────────────────────────────────

  getHkdTaxProfile(year?: number): Promise<ApiResponse<any>> {
    return api.get('/accounting/hkd-tax-regime/profile', { params: { year } }).then(r => r.data);
  },

  updateHkdTaxProfile(data: { tax_year?: number; annual_revenue_amount?: number; annual_revenue_source?: string; filing_frequency?: string; tax_code?: string; tax_authority_name?: string; note?: string }): Promise<ApiResponse<any>> {
    return api.put('/accounting/hkd-tax-regime/profile', data).then(r => r.data);
  },

  resolveAndPersistHkdTaxProfile(year?: number): Promise<ApiResponse<any>> {
    return api.post('/accounting/hkd-tax-regime/profile/resolve', null, { params: { year } }).then(r => r.data);
  },

  recalculateHkdTaxRevenue(year?: number): Promise<ApiResponse<any>> {
    return api.post('/accounting/hkd-tax-regime/profile/recalculate', { year }).then(r => r.data);
  },

  // ─── Product Type Tax Mappings ─────────────────────────────────

  getProductTypeTaxMappings(): Promise<ApiResponse<any[]>> {
    return api.get('/accounting/product-type-tax-mappings').then(r => r.data);
  },

  createProductTypeTaxMapping(data: Partial<any>): Promise<ApiResponse<any>> {
    return api.post('/accounting/product-type-tax-mappings', data).then(r => r.data);
  },

  updateProductTypeTaxMapping(id: string, data: Partial<any>): Promise<ApiResponse<any>> {
    return api.put(`/accounting/product-type-tax-mappings/${id}`, data).then(r => r.data);
  },

  // ─── VAT Audit ──────────────────────────────────────────────

  getVatAuditProducts(params?: { vat_rate?: string; tax_industry_group_id?: string; vat_source?: string; vat_reduction_eligible?: string; page?: number; limit?: number }): Promise<ApiResponse<any>> {
    return api.get('/accounting/vat-audit/products', { params }).then(r => r.data);
  },

  resolveVat(data: {
    product_id?: string;
    inventory_item_group_id?: string;
    product_type_code?: string;
    tax_industry_group_id?: string;
    manual_override_rate?: number;
    date?: string;
  }): Promise<ApiResponse<any>> {
    return api.post('/accounting/vat/resolve', data).then(r => r.data);
  },
  /**
   * Reorder categories
   * POST /accounting/categories/reorder
   */
  reorderCategories(data: { id: string; direction: 'up' | 'down' }): Promise<ApiResponse<any>> {
    return api.post('/accounting/categories/reorder', data).then(r => r.data);
  },

  // ==================== Tax Industry Groups (Nhóm ngành chịu thuế) ====================

  /**
   * Get all tax industry groups
   * GET /accounting/tax-industry-groups
   */
  getTaxIndustryGroups(): Promise<ApiResponse<any[]>> {
    return api.get('/accounting/tax-industry-groups').then(r => {
      const res = r.data as ApiResponse<any[]>;
      // Filter out HKD items (kept in hkd_industry_tax_rates collection)
      const filtered = (res.data ?? []).filter((item: any) => !String(item.code || '').startsWith('HKD_'));
      return {
        ...res,
        data: filtered.map((item: any) => ({
          ...item,
          pit_rate: item.pit_rate?.$numberDecimal
            ? parseFloat(item.pit_rate.$numberDecimal)
            : item.pit_rate,
          revenue_threshold: item.revenue_threshold?.$numberDecimal
            ? parseFloat(item.revenue_threshold.$numberDecimal)
            : item.revenue_threshold,
        })),
      };
    });
  },

  /**
   * Create new tax industry group
   * POST /accounting/tax-industry-groups
   */
  createTaxIndustryGroup(data: Partial<any>): Promise<ApiResponse<any>> {
    return api.post('/accounting/tax-industry-groups', data).then(r => r.data);
  },

  /**
   * Update tax industry group
   * PUT /accounting/tax-industry-groups/:id
   */
  updateTaxIndustryGroup(id: string, data: Partial<any>): Promise<ApiResponse<any>> {
    return api.put(`/accounting/tax-industry-groups/${id}`, data).then(r => r.data);
  },

  /**
   * Delete tax industry group
   * DELETE /accounting/tax-industry-groups/:id
   */
  deleteTaxIndustryGroup(id: string): Promise<ApiResponse<void>> {
    return api.delete(`/accounting/tax-industry-groups/${id}`).then(r => r.data);
  },

  // ==================== Excise Tax Configs (Thuế tiêu thụ đặc biệt) ====================

  /**
   * Get all excise tax configs
   * GET /accounting/excise-tax-configs
   */
  getExciseTaxConfigs(): Promise<ApiResponse<any[]>> {
    return api.get('/accounting/excise-tax-configs').then(r => {
      const res = r.data as ApiResponse<any[]>;
      return {
        ...res,
        data: res.data.map((item: any) => ({
          ...item,
          tax_rate: item.tax_rate?.$numberDecimal
            ? parseFloat(item.tax_rate.$numberDecimal)
            : item.tax_rate,
        })),
      };
    });
  },

  /**
   * Create new excise tax config
   * POST /accounting/excise-tax-configs
   */
  createExciseTaxConfig(data: Partial<any>): Promise<ApiResponse<any>> {
    return api.post('/accounting/excise-tax-configs', data).then(r => r.data);
  },

  /**
   * Update excise tax config
   * PUT /accounting/excise-tax-configs/:id
   */
  updateExciseTaxConfig(id: string, data: Partial<any>): Promise<ApiResponse<any>> {
    return api.put(`/accounting/excise-tax-configs/${id}`, data).then(r => r.data);
  },

  /**
   * Delete excise tax config
   * DELETE /accounting/excise-tax-configs/:id
   */
  deleteExciseTaxConfig(id: string): Promise<ApiResponse<void>> {
    return api.delete(`/accounting/excise-tax-configs/${id}`).then(r => r.data);
  },

  // ==================== POS Product Sync (Sản phẩm POS) ====================

  /**
   * Get all POS product sync configs
   * GET /accounting/pos-config
   */
  getPOSProductSyncs(): Promise<ApiResponse<any[]>> {
    return api.get('/accounting/pos-config').then(r => r.data);
  },

  /**
   * Create new POS product sync
   * POST /accounting/pos-config
   */
  createPOSProductSync(data: Partial<any>): Promise<ApiResponse<any>> {
    return api.post('/accounting/pos-config', data).then(r => r.data);
  },

  /**
   * Update POS product sync
   * PUT /accounting/pos-config/:id
   */
  updatePOSProductSync(id: string, data: Partial<any>): Promise<ApiResponse<any>> {
    return api.put(`/accounting/pos-config/${id}`, data).then(r => r.data);
  },

  /**
   * Delete POS product sync
   * DELETE /accounting/pos-config/:id
   */
  deletePOSProductSync(id: string): Promise<ApiResponse<void>> {
    return api.delete(`/accounting/pos-config/${id}`).then(r => r.data);
  },

  /**
   * Sync POS product now
   * POST /accounting/pos-config/:id/sync-now
   */
  syncPOSProductNow(id: string): Promise<ApiResponse<any>> {
    return api.post(`/accounting/pos-config/${id}/sync-now`).then(r => r.data);
  },

  // ==================== Warehouses (Kho hàng) ====================

  /**
   * Get all warehouses
* GET /inventory/warehouses
    */
  getWarehouses(): Promise<ApiResponse<any[]>> {
    return api.get('/inventory/warehouses').then(r => r.data);
  },

  /**
   * Create new warehouse
   * POST /inventory/warehouses
   */
  createWarehouse(data: {
    code: string;
    name: string;
    address?: string;
    manager?: string;
    phone?: string;
    is_active?: boolean;
    is_default?: boolean;
  }): Promise<ApiResponse<any>> {
    return api.post('/inventory/warehouses', {
      code: data.code,
      name: data.name,
      address: data.address,
      manager: data.manager,
      phone: data.phone,
      state: data.is_active !== false ? 'active' : 'inactive',
      is_main_warehouse: data.is_default === true,
    }).then(r => r.data);
  },

  /**
   * Update warehouse
   * PATCH /inventory/warehouses/:id
   */
  updateWarehouse(id: string, data: Partial<any>): Promise<ApiResponse<any>> {
    return api.patch(`/inventory/warehouses/${id}`, data).then(r => r.data);
  },

  /**
   * Delete warehouse
   * DELETE /inventory/warehouses/:id
   */
  deleteWarehouse(id: string): Promise<ApiResponse<any>> {
    return api.delete(`/inventory/warehouses/${id}`).then(r => r.data);
  },

  /**
   * Set warehouse as default
   * POST /inventory/warehouses/:id/set-default
   */
  setDefaultWarehouse(id: string): Promise<ApiResponse<any>> {
    return api.post(`/inventory/warehouses/${id}/set-default`).then(r => r.data);
  },

  // ==================== Document Renumbering (Đánh lại số chứng từ) ====================

  /**
   * Preview document renumbering
   * POST /accounting/documents/renumber?dryRun=true
   */
  previewRenumber(data: { from_date: string; to_date: string; docType: string; startNumber: number; prefix?: string }): Promise<ApiResponse<any>> {
    return api.post('/accounting/document-numbering/renumber', {
      doc_type: data.docType,
      from_date: data.from_date,
      to_date: data.to_date,
      start_sequence: data.startNumber,
      dry_run: true,
    }).then(r => r.data);
  },

  /**
   * Execute document renumbering
   * POST /accounting/documents/renumber
   */
  renumberDocuments(data: { from_date: string; to_date: string; docType: string; startNumber: number; prefix?: string }): Promise<ApiResponse<any>> {
    return api.post('/accounting/document-numbering/renumber', {
      doc_type: data.docType,
      from_date: data.from_date,
      to_date: data.to_date,
      start_sequence: data.startNumber,
    }).then(r => r.data);
  },

  // ==================== Number Formats (Định dạng số) ====================

  /**
   * Get all number formats
   * GET /accounting/config/number-format
   */
  getNumberFormats(): Promise<ApiResponse<any>> {
    return api.get('/accounting/config/number-format').then(r => r.data);
  },

  /**
   * Update number format
   * PUT /accounting/config/number-format/:id
   */
  updateNumberFormat(_id: string, data: Partial<any>): Promise<ApiResponse<any>> {
    return api.put('/accounting/config/number-format', data).then(r => r.data);
  },

  // ==================== Sales Channels (Kênh bán) ====================

  /**
   * Get all sales channels
   * GET /accounting/revenue-channel-config/sales-channels
   */
  getSalesChannels(): Promise<ApiResponse<any[]>> {
    return api.get('/accounting/revenue-channel-config/sales-channels').then(r => r.data);
  },

  /**
   * Create new sales channel
   * POST /accounting/revenue-channel-config
   */
  createSalesChannel(data: Partial<any>): Promise<ApiResponse<any>> {
    return api.post('/accounting/revenue-channel-config', data).then(r => r.data);
  },

  /**
   * Update sales channel
   * PUT /accounting/revenue-channel-config/:id
   */
  updateSalesChannel(id: string, data: Partial<any>): Promise<ApiResponse<any>> {
    return api.put(`/accounting/revenue-channel-config/${id}`, data).then(r => r.data);
  },

  /**
   * Delete sales channel
   * DELETE /accounting/revenue-channel-config/:id
   */
  deleteSalesChannel(id: string): Promise<ApiResponse<void>> {
    return api.delete(`/accounting/revenue-channel-config/${id}`).then(r => r.data);
  },

  // ==================== Business Locations (Địa điểm kinh doanh) ====================

  /**
   * Get all business locations
   * GET /accounting/business-locations
   */
  getBusinessLocations(): Promise<ApiResponse<any[]>> {
    return api.get('/accounting/business-locations').then(r => r.data);
  },

  /**
   * Create new business location
   * POST /accounting/business-locations
   */
  createBusinessLocation(data: Partial<any>): Promise<ApiResponse<any>> {
    return api.post('/accounting/business-locations', data).then(r => r.data);
  },

  /**
   * Update business location
   * PUT /accounting/business-locations/:id
   */
  updateBusinessLocation(id: string, data: Partial<any>): Promise<ApiResponse<any>> {
    return api.put(`/accounting/business-locations/${id}`, data).then(r => r.data);
  },

  /**
   * Delete business location
   * DELETE /accounting/business-locations/:id
   */
  deleteBusinessLocation(id: string): Promise<ApiResponse<void>> {
    return api.delete(`/accounting/business-locations/${id}`).then(r => r.data);
  },

  // ==================== Opening Balances (Số dư đầu kỳ) ====================

  /**
   * Get opening balances for all accounts
   * GET /initial-setup/opening-balances (BE uses this path)
   */
  getOpeningBalances(params?: { store_id?: string }): Promise<ApiResponse<any[]>> {
    return api.get('/initial-setup/opening-balances', { params }).then(r => r.data);
  },

  /**
   * Get opening balance history for an account
   * GET /initial-setup/opening-balances/:accountId/history
   */
  getOpeningBalanceHistory(accountId: string): Promise<ApiResponse<any>> {
    return api.get(`/initial-setup/opening-balances/${accountId}/history`).then(r => r.data);
  },

  /**
   * Update opening balance for an account
   * PUT /initial-setup/opening-balances/:accountId
   */
  updateOpeningBalance(accountId: string, data: { debit?: number; credit?: number; notes?: string; store_id?: string; as_of_date?: string }): Promise<ApiResponse<any>> {
    return api.put(`/initial-setup/opening-balances/${accountId}`, data).then(r => r.data);
  },

  appendOpeningBalanceDetail(type: string, data: Record<string, unknown>): Promise<ApiResponse<any>> {
    return api.post(`/initial-setup/opening-balances/${type}/details`, data).then(r => r.data);
  },

  updateOpeningBalanceDetail(type: string, detailId: string, data: Record<string, unknown>): Promise<ApiResponse<any>> {
    return api.put(`/initial-setup/opening-balances/${type}/details/${detailId}`, data).then(r => r.data);
  },

  deleteOpeningBalanceDetail(type: string, detailId: string, params: { store_id: string; as_of_date: string }): Promise<ApiResponse<any>> {
    return api.delete(`/initial-setup/opening-balances/${type}/details/${detailId}`, { params }).then(r => r.data);
  },

  /**
   * Bulk update opening balances
   * POST /initial-setup/opening-balances/bulk
   */
  bulkUpdateOpeningBalances(balances: Array<{ account_code: string; debit?: number; credit?: number; notes?: string }>): Promise<ApiResponse<any>> {
    return api.post('/accounting/setup/opening-balances/bulk', { items: balances }).then(r => r.data);
  },

  /**
   * Lock opening balances (prevent further edits)
   * POST /initial-setup/opening-balances/lock
   */
  lockOpeningBalances(store_id: string): Promise<ApiResponse<void>> {
    return api.post('/accounting/setup/opening-balances/lock', null, { params: { store_id } }).then(r => r.data);
  },

  unlockOpeningBalances(store_id: string): Promise<ApiResponse<void>> {
    return api.post('/accounting/setup/opening-balances/unlock', null, { params: { store_id } }).then(r => r.data);
  },

  /**
   * Get opening balance summary/totals
   * GET /initial-setup/opening-balances/summary
   */
  getOpeningBalanceSummary(params: { store_id: string }): Promise<ApiResponse<{ totalDebit: number; totalCredit: number; difference: number }>> {
    return api.get('/accounting/setup/opening-balances/summary', { params }).then(r => r.data);
  },

  /**
   * Get Inventory Opening Balance Details
   * GET /initial-setup/opening-balances/inventory/details
   */
  getInventoryOpeningDetails(params: OpeningBalanceDetailsParams): Promise<ApiResponse<{
    _id: string;
    product_id: string;
    variant_id?: string;
    variant_code?: string;
    warehouse_id: string;
    quantity: number;
    unit_cost: number;
    entry_date: string;
    entry_number: string;
  }[]>> {
    return api.get('/initial-setup/opening-balances/inventory/details', { params }).then(r => r.data);
  },

  /**
   * Get AR Opening Balance Details
   * GET /initial-setup/opening-balances/ar/details
   */
  getArOpeningDetails(params: OpeningBalanceDetailsParams): Promise<ApiResponse<{
    _id: string;
    party_id: string;
    party_code?: string;
    party_name: string;
    tenant_customer_id?: string;
    customer_id?: string;
    amount: number;
    due_date: string | null;
    original_doc_number: string;
    entry_date: string;
  }[]>> {
    return api.get('/initial-setup/opening-balances/ar/details', { params }).then(r => r.data);
  },

  /**
   * Create AR Opening Balance
   * POST /initial-setup/opening-balances/ar
   */
  createArOpening(data: { as_of_date: string; store_id?: string; items: Array<{ customer_id: string; amount: number; due_date?: string; original_doc_number?: string }>; override?: boolean }): Promise<ApiResponse<any>> {
    return api.post('/initial-setup/opening-balances/ar', data).then(r => r.data);
  },

  /**
   * Create AP Opening Balance
   * POST /initial-setup/opening-balances/ap
   */
  createApOpening(data: { as_of_date: string; store_id?: string; items: Array<{ supplier_id: string; amount: number; due_date?: string; original_doc_number?: string }>; override?: boolean }): Promise<ApiResponse<any>> {
    return api.post('/initial-setup/opening-balances/ap', data).then(r => r.data);
  },

  /**
   * Create Cash Opening Balance
   * POST /initial-setup/opening-balances/cash
   */
  createCashOpening(data: { as_of_date: string; store_id?: string; items: Array<{ account_code: string; amount: number }>; override?: boolean }): Promise<ApiResponse<any>> {
    return api.post('/initial-setup/opening-balances/cash', data).then(r => r.data);
  },

  /**
   * Create Inventory Opening Balance
   * POST /initial-setup/opening-balances/inventory
   */
  createInventoryOpening(data: { as_of_date: string; store_id?: string; items: Array<{ product_id: string; variant_id?: string; warehouse_id: string; quantity: number; unit_cost: number }>; override?: boolean; allow_existing_movements?: boolean }): Promise<ApiResponse<any>> {
    return api.post('/initial-setup/opening-balances/inventory', data).then(r => r.data);
  },

  createInventoryOpeningImportJob(data: { as_of_date: string; store_id: string; expected_count: number }): Promise<ApiResponse<InventoryOpeningImportJobStatus>> {
    return api.post('/initial-setup/opening-balances/inventory/import-jobs', data).then(r => r.data);
  },

  uploadInventoryOpeningImportRows(jobId: string, data: {
    start_index: number;
    items: Array<{ product_id: string; variant_id?: string; warehouse_id: string; quantity: number; unit_cost: number }>;
  }): Promise<ApiResponse<InventoryOpeningImportJobStatus>> {
    return api.post(`/initial-setup/opening-balances/inventory/import-jobs/${jobId}/rows`, data).then(r => r.data);
  },

  submitInventoryOpeningImportJob(jobId: string, allowExistingMovements = false): Promise<ApiResponse<InventoryOpeningImportJobStatus>> {
    return api.post(`/initial-setup/opening-balances/inventory/import-jobs/${jobId}/submit`, {
      allow_existing_movements: allowExistingMovements,
    }).then(r => r.data);
  },

  getInventoryOpeningImportJobStatus(jobId: string): Promise<ApiResponse<InventoryOpeningImportJobStatus>> {
    return api.get(`/initial-setup/opening-balances/inventory/import-jobs/${jobId}`).then(r => r.data);
  },

  /**
   * Get AP Opening Balance Details
   * GET /initial-setup/opening-balances/ap/details
   */
  getApOpeningDetails(params: OpeningBalanceDetailsParams): Promise<ApiResponse<{
    _id: string;
    party_id: string;
    party_code?: string;
    party_name: string;
    tenant_customer_id?: string;
    supplier_id?: string;
    amount: number;
    due_date: string | null;
    original_doc_number: string;
    entry_date: string;
  }[]>> {
    return api.get('/initial-setup/opening-balances/ap/details', { params }).then(r => r.data);
  },

  /**
   * Get Fixed Asset Opening Balance Details
   * GET /initial-setup/opening-balances/fixed-asset/details
   */
  getFixedAssetOpeningDetails(params: OpeningBalanceDetailsParams): Promise<ApiResponse<{
    _id: string;
    asset_code: string;
    name: string;
    original_price: number;
    accumulated_depreciation: number;
    residual_value: number;
    remaining_periods: number;
    start_use_date?: string | null;
    depreciation_method?: string;
    entry_date: string;
  }[]>> {
    return api.get('/initial-setup/opening-balances/fixed-asset/details', { params }).then(r => r.data);
  },

  /**
   * Get Tool Supply (CCDC) Opening Balance Details
   */
  getToolSupplyOpeningDetails(params: OpeningBalanceDetailsParams): Promise<ApiResponse<{
    _id: string; name: string; total_amount: number; allocated_amount: number; remaining_amount: number; remaining_periods: number; entry_date: string;
  }[]>> {
    return api.get('/initial-setup/opening-balances/tool-supply/details', { params }).then(r => r.data);
  },

  /**
   * Get Tax Opening Balance Details
   */
  getTaxOpeningDetails(params: OpeningBalanceDetailsParams): Promise<ApiResponse<{
    _id: string; account_code: string; amount: number; tax_type: string; tax_period: string; entry_date: string;
  }[]>> {
    return api.get('/initial-setup/opening-balances/tax/details', { params }).then(r => r.data);
  },

  /**
   * Get Bank Opening Balance Details
   */
  getBankOpeningDetails(params: OpeningBalanceDetailsParams): Promise<ApiResponse<{
    _id: string; bank_account_number: string; bank_name: string; amount: number; entry_date: string; fund_account_id?: string;
  }[]>> {
    return api.get('/initial-setup/opening-balances/bank/details', { params }).then(r => r.data);
  },

  /**
   * Create Fixed Asset Opening Balance
   * POST /initial-setup/opening-balances/fixed-asset
   */
  createFixedAssetOpening(data: {
    as_of_date: string;
    store_id?: string;
    items: Array<{ name: string; original_price: number; accumulated_depreciation: number; asset_code?: string; residual_value?: number; start_use_date?: string; remaining_periods?: number; depreciation_method?: string }>;
    override?: boolean;
  }): Promise<ApiResponse<any>> {
    return api.post('/initial-setup/opening-balances/fixed-asset', data).then(r => r.data);
  },

  /**
   * Create Tool Supply Opening Balance
   * POST /initial-setup/opening-balances/tool-supply
   */
  createToolSupplyOpening(data: {
    as_of_date: string;
    store_id?: string;
    items: Array<{ name: string; total_amount: number; allocated_amount: number; remaining_periods: number }>;
    override?: boolean;
  }): Promise<ApiResponse<any>> {
    return api.post('/initial-setup/opening-balances/tool-supply', data).then(r => r.data);
  },

  /**
   * Create Tax Opening Balance
   * POST /initial-setup/opening-balances/tax
   */
  createTaxOpening(data: {
    as_of_date: string;
    store_id?: string;
    items: Array<{ account_code: string; amount: number; tax_type?: string; tax_period?: string }>;
    override?: boolean;
  }): Promise<ApiResponse<any>> {
    return api.post('/initial-setup/opening-balances/tax', data).then(r => r.data);
  },

  /**
   * Create Bank Opening Balance
   * POST /initial-setup/opening-balances/bank
   */
  createBankOpening(data: {
    as_of_date: string;
    store_id?: string;
    items: Array<{ fund_account_id: string; amount: number; bank_account_number?: string; bank_name?: string }>;
    override?: boolean;
  }): Promise<ApiResponse<any>> {
    return api.post('/initial-setup/opening-balances/bank', data).then(r => r.data);
  },

  /** Branch-scoped general-account opening entry. */
  createAccountOpening(data: {
    as_of_date: string;
    store_id: string;
    items: Array<{ account_id: string; debit: number; credit: number; notes?: string }>;
    override?: boolean;
  }): Promise<ApiResponse<any>> {
    return api.post('/initial-setup/opening-balances/accounts', data).then(r => r.data);
  },
};

export default accountingSettingsService;
